// @ts-strict-ignore
import Button from 'antd/lib/button';
import Divider from 'antd/lib/divider';
import Space from 'antd/lib/space';
import Tag from 'antd/lib/tag';
import React from 'react';
import {
    PromoteToTestingTrackRequestReleaseStep,
    SubmitBetaBuildForReviewRequestReleaseStep,
    tryUpdateRelease,
} from '../../actions/release';
import {
    canSubmitBuildForBetaReview,
    isGoogleBetaIntegration,
    isTestFlightBetaIntegration,
    openLinkInNewTab,
} from '../../helpers';
import { colorForBetaBuildStatus, displayTextForBetaBuildStatus } from '../../helpers/displayHelpers';
import { AppPlatform, BetaBuild, BetaStatus, IntegrationType, TestFlightBetaBuildDetail, TestTrack } from '../../types';
import { Nullable, ReleaseComponentProps, StepRunComponentProps } from '../../typescriptHelpers';
import { antGrey, releaseStepModuleTitleStyle, runwayColors, tagStyle } from '../styles/globalStyles';
import Menu from 'antd/lib/menu';
import { displayNameForIntegrationType } from '../../helpers/integrationHelpers';
import { globalMessage } from '../../utils/globalMessage';
import { RunwayUserAction } from '../../helpers/aclHelpers';
import PermissionedActionButton from '../HOCs/PermissionedActionButton';
import { useDispatch } from 'react-redux';
import { ReleaseUpdatable } from '../../actions/updatable';
import { iconForPlatform } from '../../helpers/platformComponentHelpers';
import { reduce } from 'lodash';
import Text from 'antd/lib/typography/Text';
import { ArrowRightOutlined } from '@ant-design/icons';
import { useCallUpdatable, useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';
import { StepRunUpdatable } from '@/actions/stepRun';
import { RunwayResponseType } from '../../types';
import { PromoteBuildConfirmationModal } from '../PromoteBuildConfirmationModal';

interface ActiveBetaBuildProps extends ReleaseComponentProps {
    appBetaIntegrationType?: IntegrationType;
    activeBetaBuilds?: BetaBuild[];
    betaTestingActiveBuildIdentifier?: string;
    betaLinkUrl?: string;
    defaultTestTrack?: string;
    testTracks?: TestTrack[];
    releaseIsActive: boolean;
    stepRunComponentProps: Nullable<StepRunComponentProps>;
}

const ActiveBetaBuildHeader: React.FC<ActiveBetaBuildProps> = ({
    orgId,
    appId,
    releaseId,
    activeBetaBuilds,
    betaTestingActiveBuildIdentifier,
    betaLinkUrl,
    defaultTestTrack,
    testTracks,
    releaseIsActive,
    appBetaIntegrationType,
    stepRunComponentProps,
}: ActiveBetaBuildProps) => {
    const dispatch = useDispatch();

    const hasActiveBetaBuilds = activeBetaBuilds.length > 0;
    const activeBetaBuildAppStoreIds = activeBetaBuilds.map((build) => build.appStoreBuild.appStoreBuild.id);

    const betaBuild = activeBetaBuilds.length > 0 ? activeBetaBuilds[0] : null;
    const showViewInButton = betaBuild ? betaBuild.betaStatus !== BetaStatus.NotTesting : true;

    const betaIntegrationType =
        betaBuild ? betaBuild.appStoreBuild.appStoreBuild.integrationId : appBetaIntegrationType;

    const defaultTrack = betaTestingActiveBuildIdentifier || defaultTestTrack || 'testing';

    const testTrackOptionsMenu =
        testTracks && testTracks.length > 0 ?
            <Menu
                onClick={(item) => {
                    if (item.key) {
                        showPromotionModal(item.key);
                    }
                }}
                items={testTracks
                    .map((testTrack) => ({
                        label: `Promote to ${testTrack.name} track`,
                        key: testTrack.name,
                    }))
                    .filter((item) => !['internal', defaultTrack].includes(item.key))}
            />
        :   null;

    const submitForBetaReviewEnabled =
        (
            releaseIsActive &&
            betaIntegrationType &&
            betaBuild &&
            isTestFlightBetaIntegration(betaIntegrationType) &&
            betaBuild.isConsideredExternal
        ) ?
            canSubmitBuildForBetaReview((betaBuild.betaBuildDetails as TestFlightBetaBuildDetail).externalBuildState)
        :   false;

    const allActiveBetaBuildsAreNotTesting = reduce(
        activeBetaBuilds,
        (allAreNotTesting, build) => {
            if (!allAreNotTesting) {
                return allAreNotTesting;
            } else {
                return build.betaStatus !== BetaStatus.Testing;
            }
        },
        true,
    );

    const promoteToTestTrackEnabled =
        releaseIsActive &&
        betaIntegrationType &&
        isGoogleBetaIntegration(betaIntegrationType) &&
        allActiveBetaBuildsAreNotTesting;

    const [
        submitBetaBuildToReview,
        submitBetaBuildToReviewIsLoading,
        submitBetaBuildToReviewResponseType,
        submitBetaBuildToReviewError,
    ] = useCallUpdatable(StepRunUpdatable.SubmitBetaBuildToReview, stepRunComponentProps);

    const submitBetaBuildForReview = () => {
        const request: SubmitBetaBuildForReviewRequestReleaseStep = {
            orgId: orgId,
            appId: appId,
            releaseId: releaseId,
            buildId: betaBuild.appStoreBuild.appStoreBuild.id,
        };

        if (stepRunComponentProps) {
            submitBetaBuildToReview(request);
        } else {
            dispatch(tryUpdateRelease(ReleaseUpdatable.SubmitBetaBuildForReview, request));
        }
    };

    const submitBetaBuildForReviewIsLoadingReleaseStep = useIsLoading(ReleaseUpdatable.SubmitBetaBuildForReview);
    const submitBetaBuildForReviewSuccessReleaseStep = useUpdated(ReleaseUpdatable.SubmitBetaBuildForReview);
    const submitBetaBuildForReviewErrorReleaseStep = useError(ReleaseUpdatable.SubmitBetaBuildForReview);

    const submitBetaBuildForReviewIsLoading =
        stepRunComponentProps ? submitBetaBuildToReviewIsLoading : submitBetaBuildForReviewIsLoadingReleaseStep;
    const submitBetaBuildForReviewSuccess =
        stepRunComponentProps ? submitBetaBuildToReviewResponseType : submitBetaBuildForReviewSuccessReleaseStep;
    const submitBetaBuildForReviewError =
        stepRunComponentProps ? submitBetaBuildToReviewError : submitBetaBuildForReviewErrorReleaseStep;

    const [
        promoteBuildsToTestingTrackStepRun,
        promoteBuildsToTestingTrackIsLoadingStepRun,
        promoteBuildsToTestingTrackResponseTypeStepRun,
        promoteBuildsToTestingTrackErrorStepRun,
    ] = useCallUpdatable(StepRunUpdatable.PromoteBuildsToTestingTrack, stepRunComponentProps);

    const promoteBuildToTestingTrackLoadingReleaseStep = useIsLoading(
        ReleaseUpdatable.PromoteActiveBuildToTestingTrack,
    );
    const promoteBuildToTestingTrackSuccessReleaseStep = useUpdated(ReleaseUpdatable.PromoteActiveBuildToTestingTrack);
    const promoteBuildToTestingTrackErrorReleaseStep = useError(ReleaseUpdatable.PromoteActiveBuildToTestingTrack);

    const promoteBuildToTestingTrackLoading =
        stepRunComponentProps ?
            promoteBuildsToTestingTrackIsLoadingStepRun
        :   promoteBuildToTestingTrackLoadingReleaseStep;
    const promoteBuildToTestingTrackSuccess =
        stepRunComponentProps ?
            promoteBuildsToTestingTrackResponseTypeStepRun === RunwayResponseType.Success
        :   promoteBuildToTestingTrackSuccessReleaseStep;
    const promoteBuildToTestingTrackError =
        stepRunComponentProps ? promoteBuildsToTestingTrackErrorStepRun : promoteBuildToTestingTrackErrorReleaseStep;

    React.useEffect(() => {
        if (promoteBuildToTestingTrackSuccess) {
            globalMessage.success('Build promoted to testing track!');
            setPendingPromotionTrack(null);
        }
    }, [promoteBuildToTestingTrackSuccess]);

    React.useEffect(() => {
        if (promoteBuildToTestingTrackError) {
            globalMessage.error(promoteBuildToTestingTrackError);
        }
    }, [promoteBuildToTestingTrackError]);

    const [pendingPromotionTrack, setPendingPromotionTrack] = React.useState<string | null>(null);

    const executeBuildPromotion = (builds: string[], track: string = null) => {
        const request: PromoteToTestingTrackRequestReleaseStep = {
            buildIds: builds,
            orgId: orgId,
            appId: appId,
            releaseId: releaseId,
            track,
        };

        if (stepRunComponentProps) {
            promoteBuildsToTestingTrackStepRun(request);
        } else {
            dispatch(tryUpdateRelease(ReleaseUpdatable.PromoteActiveBuildToTestingTrack, request));
        }
    };

    const showPromotionModal = (track: string = null) => {
        setPendingPromotionTrack(track ?? defaultTrack);
    };

    const confirmPromotion = () => {
        executeBuildPromotion(
            activeBetaBuildAppStoreIds,
            pendingPromotionTrack !== defaultTrack ? pendingPromotionTrack : null,
        );
        setPendingPromotionTrack(null);
    };

    const cancelPromotion = () => {
        setPendingPromotionTrack(null);
    };

    const titleForIntegrationType = (type: IntegrationType) => {
        switch (type) {
            case IntegrationType.TestFlight:
                return 'TestFlight beta';
            case IntegrationType.GoogleBeta:
                return 'Play Console testing track';
            case IntegrationType.FirebaseBeta:
                return 'Firebase app distribution';
            default:
                return null;
        }
    };

    React.useEffect(() => {
        if (submitBetaBuildForReviewSuccess) {
            globalMessage.success(
                `Build #${betaBuild.appStoreBuild.appStoreBuild.buildNumber} has been submitted for beta review!`,
            );
        }
    }, [submitBetaBuildForReviewSuccess]);

    React.useEffect(() => {
        if (submitBetaBuildForReviewError) {
            globalMessage.error(submitBetaBuildForReviewError);
        }
    }, [submitBetaBuildForReviewError]);

    const displayText = displayTextForBetaBuildStatus(betaBuild?.betaStatus, betaBuild?.betaBuildDetails);

    return (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <Space>
                <Text style={{ ...releaseStepModuleTitleStyle }}>
                    {betaIntegrationType ? titleForIntegrationType(betaIntegrationType) : 'Beta integration'}
                </Text>
                {betaIntegrationType && betaTestingActiveBuildIdentifier && (
                    <>
                        <Divider type="vertical" style={{ backgroundColor: antGrey.grey3 }} />
                        <Button href={betaLinkUrl} target="_blank" type="link" style={{ padding: 0 }}>
                            {betaTestingActiveBuildIdentifier}
                        </Button>
                    </>
                )}
            </Space>
            <Space>
                <Tag color={colorForBetaBuildStatus(betaBuild?.betaStatus)} style={{ ...tagStyle, fontSize: 10 }}>
                    {displayText}
                    {betaBuild?.testTrack ? ` (${betaBuild?.testTrack})` : ''}
                </Tag>
                {showViewInButton && betaBuild?.betaBuildDetails?.url && (
                    <Button
                        onClick={() => openLinkInNewTab(betaBuild.betaBuildDetails.url)}
                        icon={<ArrowRightOutlined style={{ color: runwayColors.info }} />}
                    >
                        View in{' '}
                        {betaIntegrationType ?
                            displayNameForIntegrationType(betaIntegrationType)
                        :   'your beta integration'}
                    </Button>
                )}
                {betaBuild &&
                    isTestFlightBetaIntegration(betaBuild.appStoreBuild.appStoreBuild.integrationId) &&
                    betaBuild.isConsideredExternal && (
                        <PermissionedActionButton
                            actionType={RunwayUserAction.UASubmitBetaBuildForReview}
                            releaseId={releaseId}
                            disabled={!submitForBetaReviewEnabled}
                            icon={iconForPlatform(
                                AppPlatform.Ios,
                                submitForBetaReviewEnabled ? runwayColors.info : runwayColors.statusGrey,
                            )}
                            onClick={submitBetaBuildForReview}
                            loading={submitBetaBuildForReviewIsLoading}
                        >
                            Submit for beta review
                        </PermissionedActionButton>
                    )}
                {hasActiveBetaBuilds && isGoogleBetaIntegration(betaIntegrationType) && (
                    <PermissionedActionButton
                        releaseId={releaseId}
                        actionType={RunwayUserAction.UAPromoteBetaBuildToTestingTrack}
                        disabled={!promoteToTestTrackEnabled}
                        overlay={testTrackOptionsMenu}
                        onClick={() => showPromotionModal()}
                        loading={promoteBuildToTestingTrackLoading}
                    >
                        {iconForPlatform(
                            AppPlatform.Android,
                            promoteToTestTrackEnabled ? runwayColors.info : runwayColors.statusGrey,
                        )}{' '}
                        Promote to {defaultTrack} track{' '}
                    </PermissionedActionButton>
                )}
            </Space>
            <PromoteBuildConfirmationModal
                isVisible={!!pendingPromotionTrack}
                isLoading={promoteBuildToTestingTrackLoading}
                appStoreBuilds={activeBetaBuilds?.map((activeBetaBuild) => activeBetaBuild.appStoreBuild) ?? []}
                trackName={pendingPromotionTrack ?? defaultTrack}
                onConfirm={confirmPromotion}
                onCancel={cancelPromotion}
            />
        </div>
    );
};

export default ActiveBetaBuildHeader;
